document.addEventListener('alpine:init', function () {
  Alpine.store('mobileNav', { open: false });
});

/**
 * Wholesale NET coffee weight — shared helper for the 6kg minimum.
 *
 * The merchant sets Shopify's native variant weight to the *packaged* weight
 * (for DHL/PH shipping rates), so `cart.total_weight` overcounts packaging.
 * The 6kg wholesale minimum must instead measure NET coffee weight. This helper
 * supplies:
 *   - getOverrideMap(): a variantId -> netGrams map (coffee net weight), read
 *     from window.theme.wholesaleNetWeights, which snippets/js-bridge.liquid
 *     emits inline for wholesale customers. No fetch — present on page load.
 *   - computeNetTotalG(): a SYNCHRONOUS sum over the live Samita wholesale cart
 *     of (netGrams-or-native-grams × quantity), used by the checkout gate (which
 *     must decide preventDefault() synchronously) and the drawer progress bar.
 *
 * Coffee net grams always come from the authoritative override map; a line's
 * native `grams` is only the fallback for items without the metafield (non-coffee
 * is ~0g today). Quantity falls back to the drawer DOM if absent on the cart
 * object. If the map is somehow absent, coffee falls back to packaged weight —
 * i.e. lenient (won't wrongly block a paying customer). No-op for retail.
 */
(function () {
  'use strict';
  if (!(window.theme && window.theme.isWholesaleCustomer === true)) return;

  function isFiniteNum(n) {
    return typeof n === 'number' && isFinite(n);
  }

  // variantId -> net coffee grams, emitted inline by snippets/js-bridge.liquid
  // for wholesale customers. Present synchronously on page load (no fetch).
  function getOverrideMap() {
    return (window.theme && window.theme.wholesaleNetWeights) || null;
  }

  // Fallback per-line quantity from the drawer DOM, mirroring the badge code's
  // variant lookup ([samitaws-variant-id] within each price zone). Only used
  // when a Samita cart item lacks a numeric `quantity`.
  function domQtyForVariant(vid) {
    var zones = document.querySelectorAll(
      '.samitaWS-product-list .samitaWS-product-item--price'
    );
    var total = 0;
    var matched = false;
    Array.prototype.forEach.call(zones, function (zone) {
      var idEl = zone.querySelector('[samitaws-variant-id]');
      var zid = idEl ? parseInt(idEl.getAttribute('samitaws-variant-id'), 10) : NaN;
      if (zid !== vid) return;
      var line = zone.closest('.samitaWS-product-item') || zone.parentElement;
      var qtyEl =
        line &&
        (line.querySelector('[samitaWS-drawer-line-item-qty]') ||
          line.querySelector('.quantity__input'));
      var q = qtyEl ? parseInt(qtyEl.value || qtyEl.getAttribute('value'), 10) : NaN;
      if (isFiniteNum(q)) {
        total += q;
        matched = true;
      }
    });
    return matched ? total : null;
  }

  function computeNetTotalG() {
    var ws = window.Samita && window.Samita.Wholesale;
    var cart = ws && ws.cart;
    if (!cart || !Array.isArray(cart.items)) return 0;
    var map = getOverrideMap() || {};
    return cart.items.reduce(function (sum, item) {
      if (!item) return sum;
      var override = map[item.variant_id];
      var unit = isFiniteNum(override)
        ? override
        : isFiniteNum(item.grams) ? item.grams : 0;
      var qty = isFiniteNum(item.quantity)
        ? item.quantity
        : domQtyForVariant(item.variant_id);
      if (!isFiniteNum(qty)) qty = 1;
      return sum + unit * qty;
    }, 0);
  }

  window.SwevenWholesale = window.SwevenWholesale || {};
  window.SwevenWholesale.getOverrideMap = getOverrideMap;
  window.SwevenWholesale.computeNetTotalG = computeNetTotalG;
})();

/**
 * Subscription Builder — Conditional Option Logic
 *
 * On the subscription product page, when "Rare & Exclusive" is selected as
 * the Coffee Type, the Quantity and Roast dropdowns are filtered to show only
 * valid options (2 Boxes + Filter). When "Microlot" is selected, all options
 * are restored.
 *
 * Detection: identifies the subscription product by its unique option values
 * (presence of "Rare & Exclusive" in a select dropdown).
 */
(function () {
  'use strict';

  function initSubscriptionBuilder() {
    var selects = document.querySelectorAll(
      '[data-single-option-selector]'
    );
    if (selects.length < 3) return;

    var collectionSelect = null;
    var quantitySelect = null;
    var roastSelect = null;

    // Identify selects by their option values rather than position/name
    selects.forEach(function (select) {
      var values = Array.from(select.options).map(function (o) {
        return o.value.toLowerCase();
      });
      if (
        values.some(function (v) {
          return v.includes('rare') || v.includes('exclusive');
        })
      ) {
        collectionSelect = select;
      } else if (
        values.some(function (v) {
          return v.includes('box');
        })
      ) {
        quantitySelect = select;
      } else if (
        values.some(function (v) {
          return v.includes('espresso') || v.includes('mixed');
        })
      ) {
        roastSelect = select;
      }
    });

    // Only proceed if all three selects are found (subscription product)
    if (!collectionSelect || !quantitySelect || !roastSelect) return;

    // Add scoping class for CSS
    var productRoot = collectionSelect.closest('[data-product-root]');
    if (productRoot) {
      productRoot.classList.add('is-subscription-product');
    }

    // Store the full set of options for each dependent select
    var allQuantityOptions = Array.from(quantitySelect.options).map(
      function (o) {
        return { value: o.value, text: o.text };
      }
    );
    var allRoastOptions = Array.from(roastSelect.options).map(function (o) {
      return { value: o.value, text: o.text };
    });

    // Determine which options are valid for Rare & Exclusive
    var reQuantityOptions = allQuantityOptions.filter(function (o) {
      return /^2\b/.test(o.value);
    });
    var reRoastOptions = allRoastOptions.filter(function (o) {
      return o.value.toLowerCase() === 'filter';
    });

    function isRareExclusive() {
      var val = collectionSelect.value.toLowerCase();
      return val.includes('rare') || val.includes('exclusive');
    }

    function rebuildSelect(select, options, preferredValue) {
      var previousValue = select.value;

      // Clear and rebuild options
      select.innerHTML = '';
      options.forEach(function (opt) {
        var option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.text;
        if (opt.value === preferredValue) {
          option.selected = true;
        }
        select.appendChild(option);
      });

      // If no option matched preferredValue, select the first one
      if (select.value !== preferredValue && options.length > 0) {
        select.value = options[0].value;
      }

      // If the value actually changed, dispatch change event for Alpine
      if (select.value !== previousValue) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    function updateDependentOptions() {
      if (isRareExclusive()) {
        rebuildSelect(quantitySelect, reQuantityOptions, reQuantityOptions[0]?.value);
        rebuildSelect(roastSelect, reRoastOptions, reRoastOptions[0]?.value);
      } else {
        // Restore full option lists, keeping current selection if still valid
        rebuildSelect(quantitySelect, allQuantityOptions, quantitySelect.value);
        rebuildSelect(roastSelect, allRoastOptions, roastSelect.value);
      }
    }

    // Listen for changes on the Collection select
    collectionSelect.addEventListener('change', updateDependentOptions);

    // Run on initial load to handle pre-selected state
    updateDependentOptions();
  }

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSubscriptionBuilder);
  } else {
    initSubscriptionBuilder();
  }
})();

/**
 * Fade-in-up — image-with-text-overlay text content
 *
 * Observes elements with data-animate="fade-in-up" and adds .is-visible
 * when they enter the viewport, triggering the CSS keyframe animation.
 * prefers-reduced-motion is handled entirely in CSS.
 */
(function () {
  'use strict';

  function initFadeInUp() {
    var elements = document.querySelectorAll('[data-animate="fade-in-up"]');
    if (!elements.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFadeInUp);
  } else {
    initFadeInUp();
  }

  document.addEventListener('shopify:section:load', initFadeInUp);
})();

/**
 * Samita Wholesale — PDP Variant Price Fix
 *
 * Samita sets the wholesale price on initial page load, but the Shapes theme
 * uses Alpine.js reactive bindings (x-html="formatMoney(currentPrice)") that
 * overwrite it on variant change with the retail price from the product JSON.
 *
 * This observer detects when the price element is updated and re-renders it
 * as a struck-through retail price + the wholesale price for the selected
 * variant. Only runs on the PDP — collection-tile prices are handled by the
 * separate tile-range IIFE further down.
 */
(function () {
  'use strict';

  /**
   * Samita rules carry TWO parallel price structures, and they disagree:
   *
   * - `custom_price_list` — written by the current admin UI ("set Discount
   *   percentage to 0 and enter Wholesale prices in the Products section").
   *   Correct wherever present; it is what the cart/checkout path honours.
   * - `discount_for_variants` — what Samita's converted/deprecated pipeline
   *   maintains. For any rule saved through the new UI it holds garbage
   *   (`percent`/null, or the pre-conversion product's old prices), while
   *   healthy legacy rules have only this structure with correct values.
   *
   * So: prefer `custom_price_list`, fall back to `discount_for_variants`,
   * and sanity-check the result against retail — broken rule data must
   * render as "no wholesale price", never as a fake discount.
   */
  function resolveWholesalePence(configs, variantId, retailPence) {
    function fromBlocks(blocks) {
      if (!Array.isArray(blocks)) return null;
      for (var i = 0; i < blocks.length; i++) {
        var vars = blocks[i] && blocks[i].variants;
        if (!vars) continue;
        var match = vars.find(function (v) {
          return v.id === variantId;
        });
        var dg = match && match.discount_groups && match.discount_groups[0];
        if (!dg || dg.value == null) continue;
        if (dg.type === 'fixed-amount') {
          var pence = Math.round(parseFloat(dg.value) * 100);
          if (!isNaN(pence)) return pence;
        } else if (dg.type === 'percent' && retailPence) {
          var pct = parseFloat(dg.value);
          if (!isNaN(pct)) return Math.round(retailPence * (1 - pct / 100));
        }
      }
      return null;
    }

    var pence = fromBlocks(configs.custom_price_list);
    if (pence === null) pence = fromBlocks(configs.discount_for_variants);

    if (pence === null || pence <= 0) return null;
    if (retailPence && pence >= retailPence) return null;
    return pence;
  }

  function getWholesalePrice(variantId) {
    var ws = window.Samita && window.Samita.Wholesale;
    if (!ws || !ws.wholesale || !ws.product) return null;

    var productEl = document.querySelector('[samitaws-element="handle"]');
    if (!productEl) return null;

    var summary;
    try {
      summary = ws.wholesale.getSummary({
        productJson: ws.product,
        productEl: productEl,
        variant_id: variantId,
      });
    } catch (e) {
      return null;
    }
    if (!summary || !summary.configs) return null;

    var original = ws.product.variants.find(function (v) {
      return v.id === variantId;
    });
    return resolveWholesalePence(
      summary.configs,
      variantId,
      original ? original.price : null
    );
  }

  function getCurrentVariantId() {
    var params = new URLSearchParams(window.location.search);
    var id = params.get('variant');
    if (id) return parseInt(id, 10);

    var fallbackSelect = document.querySelector(
      'select[name="id"]'
    );
    if (fallbackSelect) return parseInt(fallbackSelect.value, 10);

    return null;
  }

  function initWholesalePriceFix() {
    var priceEl = document.querySelector('[data-product-price]');
    if (!priceEl) return;
    // Skip when the first match is a quick-buy tile — tile pricing is owned by the
    // tile-range IIFE below.
    if (priceEl.closest('.product-tile')) return;

    var ws = window.Samita && window.Samita.Wholesale;
    if (!ws || !ws.wholesale) return;

    var moneyFormat =
      ws.shop && ws.shop.configuration
        ? ws.shop.configuration.money_format
        : '£{{amount}}';

    function format(amountInPence) {
      return moneyFormat.replace('{{amount}}', (amountInPence / 100).toFixed(2));
    }

    var applying = false;

    var observer = new MutationObserver(function () {
      if (applying) return;

      var variantId = getCurrentVariantId();
      if (!variantId) return;

      var wholesalePrice = getWholesalePrice(variantId);
      if (wholesalePrice === null) return;

      var originalVariant = ws.product.variants.find(function (v) {
        return v.id === variantId;
      });
      var originalPrice = originalVariant ? originalVariant.price : null;

      var newHtml;
      if (originalPrice && originalPrice !== wholesalePrice) {
        newHtml =
          '<span class="samitaWS-customized-original-price">' +
          format(originalPrice) +
          '</span> <span class="samitaWS-customized-price">' +
          format(wholesalePrice) +
          '</span>';
      } else {
        newHtml = format(wholesalePrice);
      }

      if (priceEl.innerHTML !== newHtml) {
        applying = true;
        priceEl.innerHTML = newHtml;
        applying = false;
      }
    });

    observer.observe(priceEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function waitForSamita(attempts) {
    if (attempts <= 0) return;
    if (
      window.Samita &&
      window.Samita.Wholesale &&
      window.Samita.Wholesale.wholesale
    ) {
      initWholesalePriceFix();
    } else {
      setTimeout(function () {
        waitForSamita(attempts - 1);
      }, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      waitForSamita(20);
    });
  } else {
    waitForSamita(20);
  }
})();

/**
 * Samita Wholesale — Collection Tile Price Range
 *
 * On collection tiles Samita renders a single variant's wholesale price
 * (e.g. "£24.00 (struck) £14.50") regardless of how many variants the
 * product has. For multi-variant products that hides the upper end of the
 * range, so wholesale customers can't tell larger sizes exist. The
 * non-wholesale display uses a "From {min} - {max}" range; this IIFE
 * recreates that for wholesale tiles.
 *
 * Iterates each variant via Samita's getSummary() to compute min/max
 * wholesale + retail across the product, then rewrites the tile markup
 * inside Samita's [samitaws-product-id] wrapper. PDP price is excluded
 * (it lives inside [data-product-price] and is owned by the IIFE above).
 */
(function () {
  'use strict';

  // Same two-structure resolution as the PDP price fix above: prefer
  // `custom_price_list` (correct, cart-honoured), fall back to
  // `discount_for_variants` (healthy legacy rules only), reject anything
  // at/above retail. See resolveWholesalePence() in the PDP IIFE for why.
  function resolveWholesalePence(configs, variantId, retailPence) {
    function fromBlocks(blocks) {
      if (!Array.isArray(blocks)) return null;
      for (var i = 0; i < blocks.length; i++) {
        var vars = blocks[i] && blocks[i].variants;
        if (!vars) continue;
        var match = vars.find(function (v) {
          return v.id === variantId;
        });
        var dg = match && match.discount_groups && match.discount_groups[0];
        if (!dg || dg.value == null) continue;
        if (dg.type === 'fixed-amount') {
          var pence = Math.round(parseFloat(dg.value) * 100);
          if (!isNaN(pence)) return pence;
        } else if (dg.type === 'percent' && retailPence) {
          var pct = parseFloat(dg.value);
          if (!isNaN(pct)) return Math.round(retailPence * (1 - pct / 100));
        }
      }
      return null;
    }

    var pence = fromBlocks(configs.custom_price_list);
    if (pence === null) pence = fromBlocks(configs.discount_for_variants);

    if (pence === null || pence <= 0) return null;
    if (retailPence && pence >= retailPence) return null;
    return pence;
  }

  function getWholesalePriceForVariant(productJson, variantId, productEl) {
    var ws = window.Samita && window.Samita.Wholesale;
    if (!ws || !ws.wholesale) return null;

    var summary;
    try {
      summary = ws.wholesale.getSummary({
        productJson: productJson,
        productEl: productEl,
        variant_id: variantId,
      });
    } catch (e) {
      return null;
    }
    if (!summary || !summary.configs) return null;

    var original = productJson.variants.find(function (v) {
      return v.id === variantId;
    });
    return resolveWholesalePence(
      summary.configs,
      variantId,
      original ? original.price : null
    );
  }

  function applyTileRange(handleEl, ws, moneyFormat) {
    var handle = handleEl.getAttribute('data-samitaws-handle');
    if (!handle) return;

    var wrapper = handleEl.querySelector('[samitaws-product-id]');
    if (!wrapper) return;

    var product = null;
    var products = ws.products;
    if (products && products.length) {
      for (var i = 0; i < products.length; i++) {
        if (products[i] && products[i].handle === handle) {
          product = products[i];
          break;
        }
      }
    }
    if (!product || !product.variants || product.variants.length <= 1) return;

    var retailPrices = [];
    var wholesalePrices = [];
    for (var j = 0; j < product.variants.length; j++) {
      var v = product.variants[j];
      var retail = v.price;
      var wholesale = getWholesalePriceForVariant(product, v.id, handleEl);
      if (wholesale === null) wholesale = retail;
      retailPrices.push(retail);
      wholesalePrices.push(wholesale);
    }

    var retailMin = Math.min.apply(null, retailPrices);
    var retailMax = Math.max.apply(null, retailPrices);
    var wholesaleMin = Math.min.apply(null, wholesalePrices);
    var wholesaleMax = Math.max.apply(null, wholesalePrices);

    // All variants resolve to the same wholesale price → leave Samita's
    // single-price display alone.
    if (wholesaleMin === wholesaleMax) return;

    function format(amountInPence) {
      return moneyFormat.replace('{{amount}}', (amountInPence / 100).toFixed(2));
    }

    var retailHtml =
      retailMin === retailMax
        ? format(retailMax)
        : format(retailMin) + ' - ' + format(retailMax);

    var wholesaleHtml = 'From ' + format(wholesaleMin) + ' - ' + format(wholesaleMax);

    var newHtml =
      '<span class="samitaWS-customized-original-price">' +
      retailHtml +
      '</span> <span class="samitaWS-customized-price">' +
      wholesaleHtml +
      '</span>';

    if (wrapper.innerHTML.replace(/\s+/g, ' ').trim() === newHtml) return;
    wrapper.innerHTML = newHtml;
  }

  function initTileRanges() {
    var ws = window.Samita && window.Samita.Wholesale;
    if (!ws || !ws.wholesale) return;

    var moneyFormat =
      ws.shop && ws.shop.configuration
        ? ws.shop.configuration.money_format
        : '£{{amount}}';

    // Find each multi-variant tile by its [data-samitaws-handle] anchor —
    // a stable element that survives Samita's wrapper rerenders. Only target
    // tiles within .product-tile and not inside [data-product-price] (PDP /
    // quick-buy reactive prices).
    var handleEls = document.querySelectorAll('[data-samitaws-handle]');
    handleEls.forEach(function (handleEl) {
      if (!handleEl.closest('.product-tile')) return;
      if (handleEl.closest('[data-product-price]')) return;
      if (!handleEl.querySelector('[samitaws-product-id]')) return;

      var applying = false;

      function apply() {
        if (applying) return;
        applying = true;
        try {
          applyTileRange(handleEl, ws, moneyFormat);
        } finally {
          applying = false;
        }
      }

      apply();

      // Observe the stable handle anchor — Samita replaces the inner
      // [samitaws-product-id] wrapper element on rerender, so observing the
      // wrapper directly orphans us. Watching the parent catches both
      // childList replacements and innerHTML mutations.
      var observer = new MutationObserver(function () {
        if (applying) return;
        apply();
      });
      observer.observe(handleEl, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    });
  }

  function waitForSamita(attempts) {
    if (attempts <= 0) return;
    var ready =
      window.Samita &&
      window.Samita.Wholesale &&
      window.Samita.Wholesale.wholesale &&
      window.Samita.Wholesale.products &&
      window.Samita.Wholesale.products.length &&
      document.querySelector('[samitaws-product-id]');
    if (ready) {
      initTileRanges();
    } else {
      setTimeout(function () {
        waitForSamita(attempts - 1);
      }, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      waitForSamita(40);
    });
  } else {
    waitForSamita(40);
  }
})();

/**
 * Subscription frequency — price override.
 *
 * The native <select name="selling_plan"> from
 * snippets/product-block-subscription-frequency.liquid replaces the Subi
 * widget. Shapes' compiled Alpine bundle writes currentVariant.price into
 * [data-product-price] on variant change and is not selling-plan-aware,
 * so we observe the price node and re-apply the selling plan allocation
 * price (same MutationObserver pattern as the Samita wholesale fix above).
 */
(function () {
  'use strict';

  if (window.__swevenSubscriptionPriceInit) return;
  window.__swevenSubscriptionPriceInit = true;

  var productCache = null;

  function getProductHandle() {
    var form = document.querySelector('form[action*="/cart/add"][data-product-handle]');
    return form ? form.getAttribute('data-product-handle') : null;
  }

  function loadProduct(handle, cb) {
    if (productCache) return cb(productCache);
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (p) productCache = p;
        cb(productCache);
      })
      .catch(function () { cb(null); });
  }

  function getCurrentVariantId() {
    var sel = document.querySelector('select[name="id"]');
    if (sel && sel.value) return parseInt(sel.value, 10);
    var params = new URLSearchParams(window.location.search);
    var qs = params.get('variant');
    return qs ? parseInt(qs, 10) : null;
  }

  function getAllocation(product, variantId, sellingPlanId) {
    if (!product || !variantId || !sellingPlanId) return null;
    var variant = product.variants.find(function (v) { return v.id === variantId; });
    if (!variant || !variant.selling_plan_allocations) return null;
    return variant.selling_plan_allocations.find(function (a) {
      return a.selling_plan_id === sellingPlanId;
    }) || null;
  }

  function formatMoney(cents, fmt) {
    var value = (cents / 100).toFixed(2);
    return (fmt || '£{{amount}}').replace('{{amount}}', value).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  function syncFormHiddenInput(freqSelect) {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    var hidden = form.querySelector('input[name="selling_plan"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'selling_plan';
      form.appendChild(hidden);
    }
    hidden.value = freqSelect.value;
  }

  function init() {
    var freqSelect = document.querySelector('select[name="selling_plan"][data-selling-plan-selector]');
    var priceEl = document.querySelector('[data-product-price]');
    if (!freqSelect || !priceEl) return;

    // The visible <select> lives in the variant-picker block (outside the
    // add-to-cart form). Mirror its value into a hidden input inside the
    // form so the subscription signal is actually submitted.
    syncFormHiddenInput(freqSelect);
    freqSelect.addEventListener('change', function () { syncFormHiddenInput(freqSelect); });

    // Subi re-injects its own selling_plan hidden input on variant change,
    // overwriting ours. Guarantee the submitted value by re-syncing at
    // submit time (capture phase) and by observing form mutations.
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('submit', function () { syncFormHiddenInput(freqSelect); }, true);
      var formObserver = new MutationObserver(function () {
        var hidden = form.querySelector('input[name="selling_plan"]');
        if (hidden && hidden.value !== freqSelect.value) {
          hidden.value = freqSelect.value;
        }
      });
      formObserver.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    }

    var handle = getProductHandle();
    if (!handle) return;

    var moneyFormat = (window.theme && window.theme.moneyFormat) || '£{{amount}}';
    var applying = false;

    // Subscription products don't set compare_at_price on the variant, so
    // the Shapes theme never renders a [data-compare-price] element. Inject
    // our own strike element once, next to [data-product-price], and
    // manage it from here.
    function ensureCompareEl() {
      var existing = priceEl.parentElement.querySelector('[data-subscription-compare-price]');
      if (existing) return existing;
      var s = document.createElement('s');
      s.setAttribute('data-subscription-compare-price', '');
      s.className = 'text-scheme-text ml-2';
      s.style.display = 'none';
      priceEl.insertAdjacentElement('afterend', s);
      return s;
    }

    // Frequency label shown inline between the charged price and the
    // compare-at strikethrough: "£40.80 every 2 weeks £48.00".
    function ensureFrequencyEl() {
      var existing = priceEl.parentElement.querySelector('[data-subscription-frequency-text]');
      if (existing) return existing;
      var span = document.createElement('span');
      span.setAttribute('data-subscription-frequency-text', '');
      span.className = 'text-scheme-text ml-2';
      priceEl.insertAdjacentElement('afterend', span);
      return span;
    }

    function apply(product) {
      // Subi (and variant changes) can re-inject/reset the form's hidden
      // selling_plan input, so re-sync from our visible select on every apply.
      syncFormHiddenInput(freqSelect);

      var variantId = getCurrentVariantId();
      var sellingPlanId = parseInt(freqSelect.value, 10);
      var allocation = getAllocation(product, variantId, sellingPlanId);
      if (!allocation) return;

      var priceHtml = formatMoney(allocation.price, moneyFormat);
      if (priceEl.innerHTML !== priceHtml) {
        applying = true;
        priceEl.innerHTML = priceHtml;
        applying = false;
      }

      var freqText = ensureFrequencyEl();
      var selectedOption = freqSelect.options[freqSelect.selectedIndex];
      freqText.textContent = selectedOption ? selectedOption.textContent.trim() : '';

      var compareStrike = ensureCompareEl();
      if (allocation.compare_at_price && allocation.compare_at_price > allocation.price) {
        compareStrike.innerHTML = formatMoney(allocation.compare_at_price, moneyFormat);
        compareStrike.style.display = '';
      } else {
        compareStrike.style.display = 'none';
      }

      // Guarantee order: [price] [frequency] [compare-strike].
      priceEl.insertAdjacentElement('afterend', compareStrike);
      priceEl.insertAdjacentElement('afterend', freqText);
    }

    loadProduct(handle, function (product) {
      if (!product) return;

      apply(product);

      var observer = new MutationObserver(function () {
        if (applying) return;
        apply(product);
      });
      observer.observe(priceEl, { childList: true, characterData: true, subtree: true });

      freqSelect.addEventListener('change', function () { apply(product); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Klaviyo subscribe — retail newsletter forms + Samita wholesale form.
 *
 * Mirrors theme newsletter signups into a Klaviyo retail list and Samita
 * wholesale applicants into a Klaviyo wholesale list. Config lives in
 * window.theme.klaviyo (populated from theme settings in theme.liquid).
 * If any config value is missing the hook is a silent no-op.
 */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';
  var CLIENT_SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions/';
  var RETAIL_SUBMIT_TIMEOUT_MS = 1500;

  function getConfig() {
    return window.klaviyoConfig || null;
  }

  function klaviyoSubscribe(email, listId, source) {
    var cfg = getConfig();
    if (!cfg || !cfg.siteId || !listId || !email) return Promise.resolve();
    var url =
      CLIENT_SUBSCRIBE_URL + '?company_id=' + encodeURIComponent(cfg.siteId);
    return fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source,
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      }),
    }).catch(function (err) {
      console.warn('[klaviyo] subscribe failed', err);
    });
  }

  function timeout(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function getEmailValue(form) {
    var input = form.querySelector(
      'input[name="contact[email]"], input[type="email"]'
    );
    return input ? input.value.trim() : '';
  }

  function describeRetailForm(form) {
    if (form.id === 'FooterNewsletter') return 'Footer form';
    if (form.id && form.id.indexOf('customer-form-') === 0) {
      return 'Newsletter section';
    }
    return 'Retail form';
  }

  function bindRetailInterceptor(form) {
    form.addEventListener(
      'submit',
      function (e) {
        var cfg = getConfig();
        if (!cfg || !cfg.siteId || !cfg.retailListId) return;
        var email = getEmailValue(form);
        if (!email) return;

        e.preventDefault();
        var source = describeRetailForm(form);

        Promise.race([
          klaviyoSubscribe(email, cfg.retailListId, source),
          timeout(RETAIL_SUBMIT_TIMEOUT_MS),
        ]).then(function () {
          // Bypass this listener and let Shopify do its POST → redirect → success flow.
          HTMLFormElement.prototype.submit.call(form);
        });
      },
      true
    );
  }

  function initRetailForms() {
    var forms = document.querySelectorAll(
      '#FooterNewsletter, form[id^="customer-form-"]'
    );
    forms.forEach(function (form) {
      // Only bind forms that actually carry the marketing opt-in.
      if (
        !form.querySelector(
          'input[name="contact[accepts_marketing]"][value="true"]'
        )
      ) {
        return;
      }
      bindRetailInterceptor(form);
    });
  }

  function isSamitaRegistrationForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if (form.classList.contains('samitaWS-container')) return true;
    if (
      form.action &&
      form.action.indexOf('wholesale.samita.io/api/front/registrationForm') !==
        -1
    ) {
      return true;
    }
    return false;
  }

  function initWholesaleListener() {
    document.addEventListener(
      'submit',
      function (e) {
        var cfg = getConfig();
        if (!cfg || !cfg.siteId || !cfg.wholesaleListId) return;
        var form = e.target;
        if (!isSamitaRegistrationForm(form)) return;
        var emailInput = form.querySelector(
          'input[type="email"], input[name*="email" i]:not([name*="confirm" i])'
        );
        var email = emailInput ? emailInput.value.trim() : '';
        if (!email) return;
        // Do not preventDefault — Samita's own submit handler has to run.
        // keepalive:true lets the subscribe survive a Samita-initiated navigation.
        klaviyoSubscribe(email, cfg.wholesaleListId, 'Wholesale page');
      },
      true
    );
  }

  function init() {
    initRetailForms();
    initWholesaleListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Wholesale Cart bridge — Samita coexistence.
 *
 * The native theme drawer is suppressed for wholesale customers in
 * layout/theme.liquid (so the two drawers don't stack). This IIFE wires the
 * remaining pieces:
 *   1. Warm-up: on page load, mount Samita's drawer (closed) so the first
 *      cart-icon click opens it in place. Samita lazy-mounts its drawer DOM
 *      only on first cart-state change, so without this warm-up the first
 *      click after every page load would be a no-op.
 *   2. Header cart icon click → open Samita's drawer.
 *   3. shapes:cart:afteradditem → open Samita's drawer after add-to-cart.
 *   4. Samita's "Proceed to Checkout" button → enforce 6kg minimum gate.
 *
 * No-op for retail.
 */
(function () {
  'use strict';
  if (!(window.theme && window.theme.isWholesaleCustomer === true)) return;

  // Set the warm-up flag synchronously — paired with a CSS rule in
  // custom.css that hides the drawer until we've mounted+closed it.
  document.documentElement.setAttribute('data-sweven-cart-warming', '');

  function getDrawerEls() {
    var drawer = document.querySelector('.samitaWS-cart-drawer-widget');
    var container = drawer && drawer.querySelector('.samitaWS-drawer-container');
    return { drawer: drawer, container: container };
  }

  function clearStuckEmptyCartSpinner() {
    // Samita's loadCartContent doesn't clear the loading state when cart is
    // empty; defensive cleanup so the empty-state UI is visible.
    var ws = window.Samita && window.Samita.Wholesale;
    var drawer = document.querySelector('.samitaWS-cart-drawer-widget');
    if (!drawer || !ws || !ws.cart || ws.cart.item_count !== 0) return;
    drawer.querySelectorAll('.samitaWS-loading-main.show')
      .forEach(function (el) { el.classList.remove('show'); });
    drawer.querySelectorAll('.samitaWS-product-list.loading')
      .forEach(function (el) { el.classList.remove('loading'); });
  }

  function openSamitaDrawer() {
    var ws = window.Samita && window.Samita.Wholesale;
    var H = ws && ws.HandleCartDrawer;
    var els = getDrawerEls();
    if (!els.drawer || !els.container) return false;
    clearStuckEmptyCartSpinner();
    if (H && typeof H.toggleCartDrawer === 'function') {
      try {
        H.toggleCartDrawer({
          cartOverlay: els.drawer,
          cartContainer: els.container,
          open: true
        });
        return true;
      } catch (e) {}
    }
    // Fallback: manual class toggle (Samita's own CSS handles the rest).
    els.drawer.classList.add('samitaWS-drawer__expanded');
    els.container.classList.add('samitaWS-drawer__expanded');
    return true;
  }

  // (1) Warm-up: mount Samita's drawer DOM on page load and immediately
  // close it. handleCartDrawerApp does the lazy-load + builds the widget
  // when oldAddCart=true; it returns before init finishes, so we poll
  // until the widget appears in the DOM, then close it via toggleCartDrawer.
  function warmUpDrawer(done) {
    var ws = window.Samita && window.Samita.Wholesale;
    if (!ws || typeof ws.handleCartDrawerApp !== 'function') {
      done(false);
      return;
    }
    if (document.querySelector('.samitaWS-cart-drawer-widget')) {
      done(true);
      return;
    }
    try {
      ws.handleCartDrawerApp({
        productForm: false,
        productLinks: [],
        oldAddCart: true,
        ev_scroll: false,
        accountDetail: false,
        rgForms: [],
        rgShortCodeForms: [],
        quickOrderForms: [],
        quickOrderShortCodeForms: []
      });
    } catch (e) {
      done(false);
      return;
    }
    var attempts = 0;
    var poll = function () {
      attempts++;
      var els = getDrawerEls();
      if (els.drawer && els.container) {
        var H = ws.HandleCartDrawer;
        if (H && typeof H.toggleCartDrawer === 'function') {
          try {
            H.toggleCartDrawer({ cartOverlay: els.drawer, cartContainer: els.container });
          } catch (e) {}
        }
        // Workaround for a Samita bug: loadCartContent only clears the
        // loading state when cart.item_count > 0. For empty carts the
        // spinner persists, so the drawer opens to a stuck spinner instead
        // of the "YOU HAVE NO ITEMS" UI. Clean up the loading classes so
        // the empty-state markup (rendered by buildCartDrawerWidget) shows.
        if (ws.cart && ws.cart.item_count === 0) {
          els.drawer.querySelectorAll('.samitaWS-loading-main.show')
            .forEach(function (el) { el.classList.remove('show'); });
          els.drawer.querySelectorAll('.samitaWS-product-list.loading')
            .forEach(function (el) { el.classList.remove('loading'); });
        }
        // Hold the warming flag long enough for Samita's close animation
        // to finish invisibly, otherwise the user sees the drawer fade-shut
        // tail on every page load.
        setTimeout(function () { done(true); }, 350);
        return;
      }
      if (attempts >= 60) { done(false); return; }
      setTimeout(poll, 50);
    };
    poll();
  }

  function unsetWarming() {
    document.documentElement.removeAttribute('data-sweven-cart-warming');
  }

  function waitForSamitaThenWarm(attempts) {
    if (attempts <= 0) { unsetWarming(); return; }
    var ws = window.Samita && window.Samita.Wholesale;
    if (ws && typeof ws.handleCartDrawerApp === 'function' && ws.HandleCartDrawer) {
      warmUpDrawer(unsetWarming);
    } else {
      setTimeout(function () { waitForSamitaThenWarm(attempts - 1); }, 250);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      waitForSamitaThenWarm(80);
    });
  } else {
    waitForSamitaThenWarm(80);
  }

  // (2) Header cart icon — capture-phase intercept so we run before Alpine's
  // @click.prevent="$store.modals.open('cart')" handler (which is a no-op for
  // wholesale anyway since the theme drawer is suppressed).
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest('#CartButton');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (openSamitaDrawer()) return;
      // Last-resort fallback if warm-up failed entirely.
      var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
      window.location.href = cartUrl;
    },
    true
  );

  // (3) After add-to-cart, the theme bundle dispatches shapes:cart:afteradditem.
  // Samita's own bundle usually opens its drawer on add-to-cart; this is
  // defensive in case the theme drawer suppression interferes.
  document.body.addEventListener('shapes:cart:afteradditem', function () {
    setTimeout(openSamitaDrawer, 100);
  });

  // (3) 6kg minimum gate on Samita's "Proceed to Checkout".
  var minKg = parseFloat(window.theme.wholesaleMinKg) || 0;
  if (minKg > 0) {
    var minG = minKg * 1000;
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest('.samitaWS-btn-checkout');
        if (!btn) return;
        var weight;
        if (window.SwevenWholesale && window.SwevenWholesale.computeNetTotalG) {
          weight = window.SwevenWholesale.computeNetTotalG();
        } else {
          // Defensive fallback to native weight if the net-weight helper is
          // unavailable — never hard-blocks a paying wholesale customer.
          var ws = window.Samita && window.Samita.Wholesale;
          weight = (ws && ws.cart && ws.cart.total_weight) || 0;
        }
        if (weight >= minG) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        var drawer = document.querySelector('.samitaWS-cart-drawer-widget');
        if (!drawer) return;
        var notice = drawer.querySelector('[data-sweven-wholesale-block-notice]');
        if (!notice) {
          notice = document.createElement('div');
          notice.setAttribute('data-sweven-wholesale-block-notice', '');
          // Style hooks live in custom.css; minimal inline fallback in case
          // the CSS hasn't loaded.
          notice.style.cssText =
            'margin-top:0.75rem;text-align:center;font-size:0.75rem;opacity:0.8;';
          btn.parentElement.insertBefore(notice, btn);
        }
        notice.innerHTML =
          (window.theme.strings && window.theme.strings.wholesaleBlockedNoticeHtml) ||
          'Wholesale orders require a <strong>' + minKg + 'kg</strong> minimum.';
      },
      true
    );
  }
})();

/**
 * Wholesale Drawer UI — 6kg minimum progress bar + bulk-discount badges.
 *
 * Samita renders its own cart drawer and re-renders it on open and on every
 * cart change, so injected nodes are wiped. A single debounced MutationObserver
 * re-renders both widgets. Every write is guarded (compare-then-write) so an
 * unchanged render produces no DOM mutation — that is what stops the observer
 * from firing on its own output.
 *
 * Progress bar host is `.samitaWS-product`: Samita sets `.samitaWS-order-totals`
 * and `.samitaWS-header-actions` to display:none on an empty cart, so it is the
 * only container visible in both the empty and populated states.
 *
 * No-op for retail.
 */
(function () {
  'use strict';
  if (!(window.theme && window.theme.isWholesaleCustomer === true)) return;

  var PROGRESS_ID = 'sweven-ws-progress';
  var BADGE_ATTR = 'data-sweven-bulk-badge';
  var WAS_ATTR = 'data-sweven-bulk-was';

  var minKg = parseFloat(window.theme.wholesaleMinKg) || 0;
  var minG = minKg * 1000;

  function strings() {
    return (window.theme && window.theme.strings) || {};
  }

  function samitaCart() {
    var ws = window.Samita && window.Samita.Wholesale;
    return (ws && ws.cart) || null;
  }

  function moneyFormat() {
    var ws = window.Samita && window.Samita.Wholesale;
    return (
      (ws && ws.shop && ws.shop.configuration && ws.shop.configuration.money_format) ||
      window.theme.moneyFormat ||
      '£{{amount}}'
    );
  }

  function format(pence) {
    return moneyFormat().replace('{{amount}}', (pence / 100).toFixed(2));
  }

  /**
   * Inverse of format(). Only the plain `{{amount}}` money format is safely
   * reversible — the comma/period-separator variants are ambiguous about which
   * symbol is the decimal point, so we bail rather than risk a wrong figure.
   */
  function parsePence(text) {
    if (!text || moneyFormat().indexOf('{{amount}}') === -1) return null;
    var match = String(text).replace(/[\s ]/g, '').match(/[\d.,]+/);
    if (!match) return null;
    var value = parseFloat(match[0].replace(/,/g, ''));
    return isNaN(value) ? null : Math.round(value * 100);
  }

  // ---- 6kg minimum progress bar -------------------------------------------

  function buildProgress() {
    var el = document.createElement('div');
    el.id = PROGRESS_ID;
    el.className = 'sweven-ws-progress';
    el.innerHTML =
      '<div class="wholesale-progress-label" data-ws-label></div>' +
      '<div class="sweven-ws-progress__track-wrap">' +
        '<div class="wholesale-progress-track">' +
          '<div class="wholesale-progress-fill" data-ws-fill></div>' +
        '</div>' +
      '</div>';
    return el;
  }

  function renderProgress() {
    if (minG <= 0) return;

    var host =
      document.querySelector('.samitaWS-tabcontent-cart .samitaWS-product') ||
      document.querySelector('.samitaWS-product');
    if (!host) return;

    var el = document.getElementById(PROGRESS_ID);
    if (!el) {
      el = buildProgress();
      host.insertBefore(el, host.firstChild);
    } else if (el.parentElement !== host || el !== host.firstElementChild) {
      host.insertBefore(el, host.firstChild);
    }

    var cart = samitaCart();
    var weight =
      window.SwevenWholesale && window.SwevenWholesale.computeNetTotalG
        ? window.SwevenWholesale.computeNetTotalG()
        : cart && typeof cart.total_weight === 'number' ? cart.total_weight : 0;
    var pct = Math.min(100, Math.round((weight / minG) * 10000) / 100);
    var met = weight >= minG;

    var fill = el.querySelector('[data-ws-fill]');
    if (fill && fill.style.width !== pct + '%') fill.style.width = pct + '%';

    var html;
    if (met) {
      html = strings().wholesaleMinAchieved || 'Wholesale minimum met';
    } else {
      var remainingKg = Math.round(((minG - weight) / 1000) * 10) / 10;
      html = (
        strings().wholesaleMinNotAchievedHtml ||
        'Add <strong>%%AMOUNT%%</strong> more to unlock wholesale checkout'
      ).replace('%%AMOUNT%%', remainingKg + 'kg');
    }
    var label = el.querySelector('[data-ws-label]');
    if (label && label.innerHTML !== html) label.innerHTML = html;

    // Proactive blocked state. The hard gate is the click interceptor above.
    var checkout = document.querySelector('.samitaWS-order-totals .samitaWS-btn-checkout');
    if (checkout) checkout.classList.toggle('sweven-ws-blocked', !met);
  }

  // ---- Bulk-discount badges ------------------------------------------------

  /**
   * Titles of the active wholesale quantity-break campaigns, read from Pareto's
   * client-side global. Shopify stamps the campaign name onto each discount
   * allocation, so this lets us attribute a line's discount to the bulk rule
   * rather than to some other automatic discount that happens to target the
   * same line. Returns null when Pareto is absent, in which case we fall back
   * to the line's total discount.
   */
  // Pareto appends an invisible zero-width tracking suffix (U+200B–U+200D,
  // U+2060) to the discount title it stamps on cart allocations, so compare
  // titles with those stripped from both sides.
  function normalizeTitle(title) {
    return String(title || '').replace(/[​-‍⁠﻿]/g, '').trim();
  }

  function bulkCampaignTitles() {
    var data = window.omgQuantityData;
    if (!data || !Array.isArray(data.offers)) return null;
    var titles = {};
    var found = false;
    data.offers.forEach(function (o) {
      if (!o || o.status !== 1 || o.customer_tag !== 'wholesale') return;
      if (!o.campaign_name) return;
      titles[normalizeTitle(o.campaign_name)] = true;
      found = true;
    });
    return found ? titles : null;
  }

  function bulkDiscountFor(item, titles) {
    if (!item) return 0;
    var allocations = item.line_level_discount_allocations;
    if (titles && Array.isArray(allocations)) {
      return allocations.reduce(function (sum, a) {
        var app = a && a.discount_application;
        if (app && titles[normalizeTitle(app.title)]) return sum + (a.amount || 0);
        return sum;
      }, 0);
    }
    return item.total_discount || 0;
  }

  function renderBadges() {
    var cart = samitaCart();
    if (!cart || !Array.isArray(cart.items)) return;

    var zones = document.querySelectorAll(
      '.samitaWS-product-list .samitaWS-product-item--price'
    );
    if (!zones.length) return;

    var titles = bulkCampaignTitles();
    var seen = {};

    Array.prototype.forEach.call(zones, function (zone) {
      var idEl = zone.querySelector('[samitaws-variant-id]');
      var vid = idEl ? parseInt(idEl.getAttribute('samitaws-variant-id'), 10) : NaN;

      var item = null;
      if (!isNaN(vid)) {
        // A variant can appear on more than one line (different properties),
        // so consume matches in DOM order rather than always taking the first.
        var matches = cart.items.filter(function (i) {
          return i.variant_id === vid;
        });
        var nth = seen[vid] || 0;
        item = matches[nth] || matches[0] || null;
        seen[vid] = nth + 1;
      }

      var discount = item ? bulkDiscountFor(item, titles) : 0;
      renderWasPrice(zone, item, discount);
      renderBadge(zone, discount);
    });
  }

  /**
   * Crossed-out price the line would have cost without the bulk discount.
   *
   * Samita hides its own `.samitaWS-customized-original-price` inside the drawer
   * via an inline `display:none !important`. That one is the RETAIL price anyway,
   * which conflates the wholesale saving with the bulk saving. We show the
   * wholesale-before-bulk figure instead, so that `was − badge amount` equals the
   * price on screen.
   */
  function renderWasPrice(zone, item, discount) {
    var totalRow = zone.querySelector('.samitaWS-product-item--price__total');
    if (!totalRow) return;

    var existing = totalRow.querySelector('[' + WAS_ATTR + ']');

    if (!item || discount <= 0) {
      if (existing) existing.remove();
      return;
    }

    // Prefer the figure Samita actually rendered (it is wholesale-aware).
    // Products with no wholesale rule render a plain total, so fall back to
    // Shopify's retail line price.
    var finalEl = totalRow.querySelector('.samitaWS-customized-price');
    var finalPence = finalEl ? parsePence(finalEl.textContent) : null;
    if (finalPence === null && typeof item.final_line_price === 'number') {
      finalPence = item.final_line_price;
    }
    if (finalPence === null) {
      if (existing) existing.remove();
      return;
    }

    var html = format(finalPence + discount);

    if (!existing) {
      existing = document.createElement('span');
      existing.setAttribute(WAS_ATTR, '');
      existing.className = 'sweven-bulk-was';
      var anchor = finalEl || totalRow.querySelector('.total_price');
      if (!anchor) return;
      if (finalEl) anchor.parentElement.insertBefore(existing, anchor);
      else anchor.insertBefore(existing, anchor.firstChild);
    }
    if (existing.textContent !== html) existing.textContent = html;
  }

  function renderBadge(zone, discount) {
    var badge = zone.querySelector('[' + BADGE_ATTR + ']');

    if (discount <= 0) {
      if (badge) badge.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('div');
      badge.setAttribute(BADGE_ATTR, '');
      badge.className = 'sweven-bulk-badge';
      zone.appendChild(badge);
    }

    var html = (
      strings().wholesaleBulkLineBadgeHtml ||
      '10% bulk discount · you save <strong>%%AMOUNT%%</strong>'
    ).replace('%%AMOUNT%%', format(discount));
    if (badge.innerHTML !== html) badge.innerHTML = html;
  }

  // ---- Shared debounced observer -------------------------------------------

  var scheduled = false;

  function render() {
    renderProgress();
    renderBadges();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      render();
    });
  }

  var widgetObserver = null;
  var observedWidget = null;

  function ensureAttached() {
    var widget = document.querySelector('.samitaWS-cart-drawer-widget');
    if (!widget) return false;
    if (widget === observedWidget) return true;

    // Samita rebuilds the whole widget element (e.g. on add-to-cart), which
    // detaches any observer bound to the previous node. Re-bind to the new one.
    if (widgetObserver) widgetObserver.disconnect();
    observedWidget = widget;
    widgetObserver = new MutationObserver(schedule);
    widgetObserver.observe(widget, { childList: true, subtree: true });
    schedule();
    return true;
  }

  // The widget is a direct child of <body>, so a shallow childList observer is
  // enough to catch Samita swapping it out.
  new MutationObserver(ensureAttached).observe(document.body, { childList: true });

  // …and poll while it lazy-mounts on first load.
  (function pollAttach(attempts) {
    if (ensureAttached()) return;
    if (attempts <= 0) return;
    setTimeout(function () {
      pollAttach(attempts - 1);
    }, 250);
  })(120);
})();

/**
 * Wholesale PDP — Add-to-Cart loading state + bulk-discount hint.
 *
 * (1) Loading state. Samita replaces the theme's ATC button with a clone and
 *     handles the click itself, so the theme's Product.submitForm() never runs
 *     and `loading` is never set. Samita's own busy state clears after ~660ms
 *     but its drawer doesn't open until ~2.3s, leaving a dead window where the
 *     button reads "Add to Cart" and nothing appears to happen.
 *
 *     The clone lives inside [data-product-root] and Alpine's MutationObserver
 *     initialises it, so its <span x-text="addToCartText"> is fully reactive —
 *     setting `loading` on the Product component is enough to render "Loading".
 *     We hold that until Samita's drawer finishes opening.
 *
 *     Note `shapes:cart:afteradditem` does NOT fire on wholesale PDP adds
 *     (Samita intercepts the click), so the drawer's expanded class is the
 *     only reliable completion signal.
 *
 * (2) Bulk hint. Reads Pareto's `window.omgQuantityData` to find the qty
 *     threshold for the selected variant. Renders nothing when Pareto is
 *     missing, its shape changes, or the variant has no campaign — a bad read
 *     degrades to no hint, never a wrong hint.
 *
 * No-op for retail.
 */
(function () {
  'use strict';
  if (!(window.theme && window.theme.isWholesaleCustomer === true)) return;

  var BUSY_CLASS = 'sweven-atc-busy';
  var HINT_ATTR = 'data-sweven-bulk-hint';
  var BUSY_TIMEOUT_MS = 8000;

  function alpineData(root) {
    try {
      return window.Alpine && window.Alpine.$data ? window.Alpine.$data(root) : null;
    } catch (e) {
      return null;
    }
  }

  function strings() {
    return (window.theme && window.theme.strings) || {};
  }

  // ---- (1) Add-to-Cart loading state ---------------------------------------

  var busyBtn = null;
  var busyRoot = null;
  var busyTimer = null;
  var drawerObserver = null;

  function endLoading() {
    if (busyTimer) {
      clearTimeout(busyTimer);
      busyTimer = null;
    }
    if (drawerObserver) {
      drawerObserver.disconnect();
      drawerObserver = null;
    }
    if (busyRoot) {
      var data = alpineData(busyRoot);
      if (data) {
        try {
          data.loading = false;
        } catch (e) {}
      }
    }
    if (busyBtn) {
      busyBtn.classList.remove(BUSY_CLASS);
      busyBtn.removeAttribute('aria-busy');
    }
    busyBtn = null;
    busyRoot = null;
  }

  function drawerIsOpen() {
    var w = document.querySelector('.samitaWS-cart-drawer-widget');
    return !!(w && w.classList.contains('samitaWS-drawer__expanded'));
  }

  function watchForDrawer() {
    if (drawerObserver) drawerObserver.disconnect();
    drawerObserver = new MutationObserver(function () {
      if (drawerIsOpen()) endLoading();
    });
    // The widget lazy-mounts, so watch the body rather than the widget itself.
    drawerObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('.samitaWS-clone-atc-button');
      if (!btn || btn.classList.contains(BUSY_CLASS)) return;

      // PDP only — skip quick-buy tiles.
      var root = btn.closest('[data-product-root]');
      if (!root || btn.closest('.product-tile')) return;

      var data = alpineData(root);
      if (!data) return; // Fail open: let Samita behave exactly as it does today.

      try {
        data.loading = true;
      } catch (err) {
        return;
      }

      busyBtn = btn;
      busyRoot = root;
      btn.classList.add(BUSY_CLASS);
      btn.setAttribute('aria-busy', 'true');

      watchForDrawer();
      busyTimer = setTimeout(endLoading, BUSY_TIMEOUT_MS);
    },
    true // capture: run before Samita's own handler, but never preventDefault
  );

  document.body.addEventListener('shapes:cart:adderror', endLoading);

  // ---- (2) PDP bulk-discount hint ------------------------------------------

  function currentVariantId(root) {
    var data = alpineData(root);
    if (data && data.currentVariant && data.currentVariant.id) {
      return Number(data.currentVariant.id);
    }
    var param = new URLSearchParams(window.location.search).get('variant');
    if (param) return parseInt(param, 10);
    var field = document.querySelector('input[name="id"], select[name="id"]');
    if (field && field.value) return parseInt(field.value, 10);
    return null;
  }

  function moneyFormat() {
    var ws = window.Samita && window.Samita.Wholesale;
    return (
      (ws && ws.shop && ws.shop.configuration && ws.shop.configuration.money_format) ||
      window.theme.moneyFormat ||
      '£{{amount}}'
    );
  }

  function format(pence) {
    return moneyFormat().replace('{{amount}}', (pence / 100).toFixed(2));
  }

  function parsePence(text) {
    if (!text || moneyFormat().indexOf('{{amount}}') === -1) return null;
    var match = String(text).replace(/[\s ]/g, '').match(/[\d.,]+/);
    if (!match) return null;
    var value = parseFloat(match[0].replace(/,/g, ''));
    return isNaN(value) ? null : Math.round(value * 100);
  }

  function offerForVariant(variantId) {
    var data = window.omgQuantityData;
    if (!variantId || !data || !Array.isArray(data.offers)) return null;

    for (var i = 0; i < data.offers.length; i++) {
      var o = data.offers[i];
      if (!o || o.status !== 1 || o.customer_tag !== 'wholesale') continue;
      if (!Array.isArray(o.variant_list)) continue;

      var matches = o.variant_list.some(function (v) {
        return v && Number(v.variant_id) === variantId;
      });
      if (!matches) continue;

      var rule = Array.isArray(o.discount_rule) ? o.discount_rule[0] : null;
      var qb = rule && rule.quantity_break;
      var buyFrom = qb ? parseInt(qb.buy_from, 10) : NaN;
      if (!buyFrom || isNaN(buyFrom)) continue;

      var value = qb.discount_value != null ? parseFloat(qb.discount_value) : NaN;
      return {
        buyFrom: buyFrom,
        discountUnit: qb.discount_unit || null,
        discountValue: isNaN(value) ? null : value,
      };
    }
    return null;
  }

  /**
   * Unit price once the quantity break applies, or null when it can't be
   * derived safely.
   *
   * We only compute from `.samitaWS-customized-price` (Samita's wholesale
   * figure). Before Samita's PDP price fix runs, the node briefly holds the
   * plain RETAIL price — deriving from that would flash a wrong "£14.10 each"
   * before correcting to £8.10. Requiring the wholesale node means we render
   * the text-only hint until the real price is on screen.
   */
  function bulkUnitPrice(priceEl, offer) {
    var discEl = priceEl.querySelector('.samitaWS-customized-price');
    if (!discEl) return null;

    var base = parsePence(discEl.textContent);
    if (base === null || !offer.discountValue) return null;

    var result = null;
    if (offer.discountUnit === 'price') {
      result = base - Math.round(offer.discountValue * 100);
    } else if (offer.discountUnit === 'percent') {
      result = Math.round(base * (1 - offer.discountValue / 100));
    }
    return result !== null && result > 0 ? result : null;
  }

  function renderHint(root, priceEl) {
    var parent = priceEl.parentElement;
    if (!parent) return;

    var offer = offerForVariant(currentVariantId(root));
    var el = parent.querySelector('[' + HINT_ATTR + ']');

    if (!offer) {
      if (el) el.remove();
      return;
    }

    if (!el) {
      el = document.createElement('div');
      el.setAttribute(HINT_ATTR, '');
      el.className = 'sweven-pdp-bulk-hint';
      // Sibling of the price node, never a child: the PDP price-fix observer
      // above rewrites the price element's innerHTML.
      parent.insertBefore(el, priceEl.nextSibling);
    }

    var unit = bulkUnitPrice(priceEl, offer);
    var html;
    if (unit !== null) {
      html = (
        strings().wholesaleBulkPdpHintPriceHtml ||
        'Buy %%QUANTITY%%+ and save an extra <strong>10%</strong> — <strong>%%PRICE%%</strong> each'
      )
        .replace('%%QUANTITY%%', offer.buyFrom)
        .replace('%%PRICE%%', format(unit));
    } else {
      html = (
        strings().wholesaleBulkPdpHintHtml ||
        'Buy %%QUANTITY%%+ and save an extra <strong>10%</strong>'
      ).replace('%%QUANTITY%%', offer.buyFrom);
    }
    if (el.innerHTML !== html) el.innerHTML = html;
  }

  function initHint(attempts) {
    var root = document.querySelector('[data-product-root]');
    if (!root) return;

    var priceEl = document.querySelector('[data-product-price]');
    if (!priceEl || priceEl.closest('.product-tile')) return;

    // Pareto's bundle loads async; wait for it before the first paint, but
    // don't block the observer forever if it never arrives.
    if (!window.omgQuantityData && attempts > 0) {
      setTimeout(function () {
        initHint(attempts - 1);
      }, 400);
      return;
    }

    renderHint(root, priceEl);

    // Alpine rewrites the price node on every variant change; re-render then.
    new MutationObserver(function () {
      renderHint(root, priceEl);
    }).observe(priceEl, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initHint(40);
    });
  } else {
    initHint(40);
  }
})();
